import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Backdrop,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, orange, blue } from "@material-ui/core/colors";

// Icons
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LaunchIcon from "@material-ui/icons/Launch";
import ZoomInIcon from "@material-ui/icons/ZoomIn";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

// Função para gerar URL correta da imagem
const getImageUrl = (mediaPath) => {
  if (!mediaPath) return null;
  
  console.log('🖼️ [DEBUG] mediaPath recebido:', mediaPath);
  
  // Aplica a mesma correção que funciona no MediaComponents
  const correctedUrl = mediaPath.replace('localhost:443/', 'localhost:8080/');
  
  console.log('🖼️ [DEBUG] URL corrigida:', correctedUrl);
  return correctedUrl;
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 650,
    width: "95vw",
    backgroundColor: theme.palette.background.paper,
  },

  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    "& .MuiTypography-h6": {
      fontWeight: 600,
      fontSize: "1.2rem",
    }
  },

  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },

  contentContainer: {
    display: "flex",
    gap: theme.spacing(3),
    alignItems: "flex-start",
  },

  mediaContainer: {
    flex: "0 0 200px",
    maxWidth: 200,
  },

  textContainer: {
    flex: 1,
  },

  announcementCard: {
    backgroundColor: theme.palette.background.paper,
  },

  priorityChip: {
    fontWeight: 600,
    "& .MuiChip-icon": {
      fontSize: "1.2rem",
    }
  },

  priorityHigh: {
    backgroundColor: red[50],
    color: red[600],
    border: `1px solid ${red[200]}`,
  },

  priorityMedium: {
    backgroundColor: orange[50],
    color: orange[600],
    border: `1px solid ${orange[200]}`,
  },

  priorityLow: {
    backgroundColor: green[50],
    color: green[600],
    border: `1px solid ${green[200]}`,
  },

  categoryChip: {
    fontSize: "0.75rem",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },

  mediaImage: {
    width: "100%",
    height: "auto",
    maxHeight: 300,
    objectFit: "cover",
    borderRadius: 4,
  },

  textContent: {
    lineHeight: 1.6,
    whiteSpace: "pre-line",
    color: theme.palette.text.primary,
  },

  externalLink: {
    marginTop: theme.spacing(2),
    "& .MuiButton-root": {
      textTransform: "none",
      fontWeight: 600,
    }
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
  },

  actionButton: {
    fontWeight: 600,
    minWidth: 100,
  },

  acknowledgeButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    }
  },

  dismissButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    }
  },

  viewLaterButton: {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.light,
      borderColor: theme.palette.warning.dark,
    }
  },

  timer: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },

  // Estilos para o modal de imagem em tela cheia
  imageBackdrop: {
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
  },

  fullscreenImageContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    padding: theme.spacing(4),
  },

  fullscreenImage: {
    maxWidth: "95%",
    maxHeight: "95%",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
  },

  closeFullscreenButton: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    }
  },

  imageHoverOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.3s ease",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": {
      opacity: 1,
    }
  },

  zoomIcon: {
    color: "white",
    fontSize: "2rem",
  }
}));

// Função para renderizar chip de prioridade
const renderPriorityChip = (priority, classes) => {
  const priorityConfig = {
    1: { label: "Alta", icon: <PriorityHighIcon />, className: classes.priorityHigh },
    2: { label: "Média", icon: <InfoIcon />, className: classes.priorityMedium },
    3: { label: "Baixa", icon: <CheckCircleIcon />, className: classes.priorityLow },
  };

  const config = priorityConfig[priority] || priorityConfig[2];

  return (
    <Chip
      label={config.label}
      size="small"
      icon={config.icon}
      className={`${classes.priorityChip} ${config.className}`}
    />
  );
};

// Função para renderizar chip de categoria
const renderCategoryChip = (category, classes) => {
  const categoryConfig = {
    general: "📋 Geral",
    maintenance: "🔧 Manutenção",
    update: "🆙 Atualização",
    news: "📰 Novidades",
    alert: "⚠️ Alerta",
    promotion: "🎉 Promoção",
  };

  return (
    <Chip
      label={categoryConfig[category] || "📋 Geral"}
      size="small"
      variant="outlined"
      className={classes.categoryChip}
    />
  );
};

const AnnouncementDisplayModal = ({ 
  externalAnnouncements = null, 
  isExternal = false, 
  onExternalClose = null 
}) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Busca anúncios válidos ao montar o componente ou usa anúncios externos
  useEffect(() => {
    // Se for uso externo (popover), usa os anúncios fornecidos
    if (isExternal && externalAnnouncements) {
      setAnnouncements(externalAnnouncements);
      setCurrentIndex(0);
      setOpen(true);
      
      // CORREÇÃO: Para uso externo (popover), NÃO registrar visualização automaticamente
      // A visualização será registrada apenas quando o usuário tomar uma ação
      const firstAnnouncement = externalAnnouncements[0];
      if (firstAnnouncement) {
        console.log("🔔 Popover aberto com anúncio:", firstAnnouncement.id, "- Aguardando ação do usuário");
      }
      return;
    }

    // Lógica original para modal automático após login
    const fetchModalAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcement-views/valid", {
          params: {
            onlyModal: true
          }
        });

        if (data && data.length > 0) {
          setAnnouncements(data);
          setCurrentIndex(0);
          setOpen(true);
          
          // CORREÇÃO: NÃO registrar visualização automaticamente ao abrir o modal
          // A visualização será registrada apenas quando o usuário confirmar ou dispensar
          const firstAnnouncement = data[0];
          console.log("📋 Modal aberto com anúncio:", firstAnnouncement.id, "- Aguardando ação do usuário");
          
          // Se o primeiro anúncio tem persistência, inicia o timer
          if (firstAnnouncement.requiresPersistence && firstAnnouncement.persistenceTimer > 0) {
            setTimeLeft(firstAnnouncement.persistenceTimer);
            startPersistenceTimer(firstAnnouncement.persistenceTimer);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar anúncios para modal:", error);
      }
    };

    // Só busca se o usuário estiver logado e tiver empresa (modal automático)
    if (!isExternal && user?.id && user?.companyId) {
      // Delay para garantir que o layout foi carregado
      const timeout = setTimeout(fetchModalAnnouncements, 2000);
      return () => clearTimeout(timeout);
    }
  }, [user, isExternal, externalAnnouncements]);

  // Função para verificar se um anúncio deve aparecer no modal (consultando API)
  const checkIfAnnouncementShouldShowInModal = async (announcementId) => {
    try {
      const { data } = await api.get("/announcement-views/valid", {
        params: {
          onlyModal: true  // Buscar apenas anúncios válidos para modal
        }
      });
      
      // Se a API retornou o anúncio, é porque ele deve aparecer no modal
      return data && data.length > 0 && data.find(a => a.id === announcementId);
    } catch (error) {
      console.error("❌ Erro ao verificar validade do anúncio para modal:", error);
      return false;
    }
  };

  // Socket listener para novos anúncios em tempo real (APENAS para modal principal, não externo)
  useEffect(() => {
    if (!isExternal && user?.companyId && socket) {
      const onCompanyAnnouncementNotification = async (data) => {
        if (data.action === "create" || data.action === "update") {
          const announcement = data.record;
          
          console.log("🔔 Modal recebeu notificação de anúncio:", announcement.id, "- Verificando na API se deve exibir");
          
          // Consulta API para verificar se deve mostrar no modal
          const validAnnouncement = await checkIfAnnouncementShouldShowInModal(announcement.id);
          
          if (validAnnouncement) {
            console.log("✅ API confirmou: Anúncio deve aparecer no modal:", announcement.id);
            
            // Se já tem um modal aberto, adiciona à lista para mostrar depois
            if (open && announcements.length > 0) {
              console.log("📋 Modal já aberto, adicionando à fila:", announcement.id);
              setAnnouncements(prev => [...prev, validAnnouncement]);
            } else {
              // Abre modal imediatamente com o novo anúncio
              console.log("🚀 Abrindo modal imediatamente para:", announcement.id);
              setAnnouncements([validAnnouncement]);
              setCurrentIndex(0);
              setOpen(true);
              
              // Configura timer se necessário
              if (validAnnouncement.requiresPersistence && validAnnouncement.persistenceTimer > 0) {
                setTimeLeft(validAnnouncement.persistenceTimer);
                startPersistenceTimer(validAnnouncement.persistenceTimer);
              } else {
                setTimeLeft(0);
              }
            }
          } else {
            console.log("❌ API confirmou: Anúncio não deve aparecer no modal:", announcement.id);
          }
        }
      };

      socket.on(`company-announcement-notification`, onCompanyAnnouncementNotification);

      return () => {
        socket.off(`company-announcement-notification`, onCompanyAnnouncementNotification);
      };
    }
  }, [user, socket, isExternal, open, announcements.length]);

  // Timer de persistência
  const startPersistenceTimer = (seconds) => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTimer(interval);
  };

  // Limpa timer ao desmontar
  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timer]);

  const handleClose = async () => {
    // 📋 REGISTRAR VISUALIZAÇÃO ao fechar modal (para modais sem confirmação obrigatória)
    if (announcements.length > 0 && !isExternal) {
      const currentAnnouncement = announcements[currentIndex];
      
      // Se não requer confirmação obrigatória, registrar visualização simples
      if (!currentAnnouncement.requiresAcknowledge) {
        try {
          await api.post("/announcement-views", {
            announcementId: currentAnnouncement.id
          });
          console.log("✅ [CLOSE] Visualização registrada ao fechar modal:", currentAnnouncement.id);
        } catch (error) {
          console.error("❌ [CLOSE] Erro ao registrar visualização:", error);
        }
      }
    }
    
    // Limpar timer e estado
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
    setOpen(false);
    setAnnouncements([]);
    setCurrentIndex(0);
    setTimeLeft(0);
    setFullscreenImage(null);
    
    // Chama callback externo se fornecido
    if (isExternal && onExternalClose) {
      onExternalClose();
    }
  };

  const handleImageClick = (imageSrc) => {
    setFullscreenImage(imageSrc);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  // 🔗 Função para registrar clique em link externo
  const handleLinkClick = async (announcement) => {
    console.log("🔗 [LINK CLICK FRONTEND] Iniciando registro de clique:", {
      announcementId: announcement.id,
      url: announcement.externalUrl,
      isExternal: isExternal
    });
    
    // SEMPRE registrar clique - tanto para modal quanto para popover
    try {
      console.log("📡 [LINK CLICK FRONTEND] Enviando requisição para API...");
      const response = await api.post(`/announcement-views/${announcement.id}/click-link`);
      console.log("✅ [LINK CLICK FRONTEND] Resposta da API:", response.data);
    } catch (error) {
      console.error("❌ [LINK CLICK FRONTEND] Erro ao registrar clique:", error);
      console.error("❌ [LINK CLICK FRONTEND] Detalhes do erro:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      // Não bloquear abertura do link mesmo com erro no registro
    }
    
    // Abrir link independente do resultado do registro
    try {
      console.log("🚀 [LINK CLICK FRONTEND] Abrindo link:", announcement.externalUrl);
      window.open(
        announcement.externalUrl,
        announcement.openInNewTab ? '_blank' : '_self'
      );
      console.log("✅ [LINK CLICK FRONTEND] Link aberto com sucesso");
    } catch (error) {
      console.error("❌ [LINK CLICK FRONTEND] Erro ao abrir link:", error);
    }
  };

  const handleNext = async () => {
    const currentAnnouncement = announcements[currentIndex];
    
    // Registra visualização e confirmação se necessário
    if (currentAnnouncement.requiresAcknowledge) {
      try {
        // Primeiro registra a visualização
        await api.post("/announcement-views", {
          announcementId: currentAnnouncement.id
        });
        console.log("✅ Visualização registrada para anúncio:", currentAnnouncement.id);
        
        // Depois registra a confirmação
        await api.post(`/announcement-views/${currentAnnouncement.id}/acknowledge`);
        console.log("✅ Confirmação registrada para anúncio:", currentAnnouncement.id);
      } catch (error) {
        console.error("❌ Erro ao registrar visualização/confirmação:", error);
      }
    }

    // Para uso externo (popover), fecha o modal e notifica callback
    if (isExternal) {
      handleClose();
      
      // CORREÇÃO: Notificar o popover para atualizar a lista
      if (onExternalClose) {
        // Aguardar um pouco para o backend processar
        setTimeout(() => {
          onExternalClose(); 
        }, 500);
      }
      return;
    }

    // Verifica se há próximo anúncio (apenas para uso interno)
    if (currentIndex < announcements.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      
      // CORREÇÃO: NÃO registrar visualização automaticamente do próximo anúncio
      // A visualização será registrada apenas quando o usuário tomar uma ação
      const nextAnnouncement = announcements[nextIndex];
      console.log("➡️ Passando para próximo anúncio:", nextAnnouncement.id, "- Aguardando ação do usuário");
      
      // Configura timer para o próximo anúncio se necessário
      if (nextAnnouncement.requiresPersistence && nextAnnouncement.persistenceTimer > 0) {
        setTimeLeft(nextAnnouncement.persistenceTimer);
        startPersistenceTimer(nextAnnouncement.persistenceTimer);
      } else {
        if (timer) {
          clearInterval(timer);
          setTimer(null);
        }
        setTimeLeft(0);
      }
    } else {
      // Último anúncio, fecha o modal
      handleClose();
    }
  };

  // Nova função para "Ver depois" - REGISTRA visualização mas NÃO confirma
  const handleViewLater = async () => {
    console.log("👀 Usuário escolheu 'Ver depois' - registrando visualização mas NÃO confirmando");
    
    const currentAnnouncement = announcements[currentIndex];
    
    // SEMPRE registrar visualização - tanto para modal quanto para popover
    try {
      await api.post("/announcement-views", {
        announcementId: currentAnnouncement.id
      });
      console.log("✅ Visualização registrada (Ver depois) para anúncio:", currentAnnouncement.id);
    } catch (error) {
      console.error("❌ Erro ao registrar visualização:", error);
    }
    
    // Para uso externo (popover), fecha o modal e força callback
    if (isExternal) {
      handleClose();
      
      // CORREÇÃO: Notificar o popover para atualizar a lista
      if (onExternalClose) {
        // Aguardar um pouco para o backend processar
        setTimeout(() => {
          onExternalClose(); 
        }, 500);
      }
      return;
    }
    
    // Verifica se há próximo anúncio (apenas para uso interno)
    if (currentIndex < announcements.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      
      // CORREÇÃO: NÃO registrar visualização do próximo anúncio quando é "Ver depois"
      // Isso permite que anúncios que exigem confirmação continuem aparecendo
      const nextAnnouncement = announcements[nextIndex];
      console.log("➡️ Passando para próximo anúncio sem registrar visualização:", nextAnnouncement.id);
      
      // Configura timer para o próximo anúncio se necessário
      if (nextAnnouncement.requiresPersistence && nextAnnouncement.persistenceTimer > 0) {
        setTimeLeft(nextAnnouncement.persistenceTimer);
        startPersistenceTimer(nextAnnouncement.persistenceTimer);
      } else {
        if (timer) {
          clearInterval(timer);
          setTimer(null);
        }
        setTimeLeft(0);
      }
    } else {
      // Último anúncio, fecha o modal SEM registrar como lida
      handleClose();
    }
  };

  const handleDismiss = async () => {
    const currentAnnouncement = announcements[currentIndex];
    
    // Se não bloqueia dispensar para sempre, permite fechar
    if (!currentAnnouncement.blockDismissForever) {
      try {
        // ⚠️ IMPORTANTE: handleClose() já registra visualização, então aqui só precisamos
        // registrar o dismiss forever se permitido
        if (currentAnnouncement.allowDismissForever) {
          // Primeiro registra visualização (caso não tenha sido registrada ainda)
          await api.post("/announcement-views", {
            announcementId: currentAnnouncement.id
          });
          console.log("✅ [DISMISS] Visualização garantida para anúncio:", currentAnnouncement.id);
          
          // Depois registra dismiss forever
          await api.post(`/announcement-views/${currentAnnouncement.id}/dismiss-forever`);
          console.log("✅ [DISMISS] Anúncio dispensado para sempre:", currentAnnouncement.id);
        }
      } catch (error) {
        console.error("❌ [DISMISS] Erro ao dispensar anúncio:", error);
      }
      
      // Fechar modal (handleClose NÃO vai registrar visualização novamente 
      // porque dismiss forever já garante que foi registrada)
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setOpen(false);
      setAnnouncements([]);
      setCurrentIndex(0);
      setTimeLeft(0);
      setFullscreenImage(null);
      
      if (isExternal && onExternalClose) {
        onExternalClose();
      }
    }
  };

  if (!open || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  const hasNext = currentIndex < announcements.length - 1;

  return (
    <>
      <Dialog
        open={open}
        onClose={currentAnnouncement.requiresAcknowledge ? undefined : handleDismiss}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
        disableEscapeKeyDown={currentAnnouncement.requiresAcknowledge}
        disableBackdropClick={currentAnnouncement.requiresAcknowledge}
      >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon />
          {currentAnnouncement.title}
          {announcements.length > 1 && (
            <Typography variant="caption" style={{ marginLeft: 'auto', opacity: 0.8 }}>
              {currentIndex + 1} de {announcements.length}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={currentAnnouncement.mediaPath ? classes.contentContainer : null}>
          {/* Imagem à esquerda se houver */}
          {currentAnnouncement.mediaPath && (
            <Box className={classes.mediaContainer}>
              <Box style={{ position: "relative" }}>
                <img
                  src={getImageUrl(currentAnnouncement.mediaPath)}
                  alt="Anexo do anúncio"
                  className={classes.mediaImage}
                  onError={(e) => {
                    console.error('❌ Erro ao carregar imagem:', e.target.src);
                    console.error('❌ mediaPath original:', currentAnnouncement.mediaPath);
                  }}
                  onLoad={() => {
                    console.log('✅ Imagem carregada com sucesso:', getImageUrl(currentAnnouncement.mediaPath));
                  }}
                />
                <Box 
                  className={classes.imageHoverOverlay}
                  onClick={() => handleImageClick(getImageUrl(currentAnnouncement.mediaPath))}
                >
                  <ZoomInIcon className={classes.zoomIcon} />
                </Box>
              </Box>
            </Box>
          )}

          {/* Conteúdo à direita */}
          <Box className={classes.textContainer}>
            {/* Chips de prioridade e categoria */}
            <Box display="flex" alignItems="center" flexWrap="wrap" marginBottom={2}>
              <Box marginRight={1}>
                {renderPriorityChip(currentAnnouncement.priority, classes)}
              </Box>
              {currentAnnouncement.category && (
                <Box>
                  {renderCategoryChip(currentAnnouncement.category, classes)}
                </Box>
              )}
            </Box>

            {/* Conteúdo do texto */}
            <Typography
              variant="body1"
              className={classes.textContent}
            >
              {currentAnnouncement.text}
            </Typography>

            {/* Link externo se houver */}
            {currentAnnouncement.externalUrl && (
              <Box className={classes.externalLink}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<LaunchIcon />}
                  onClick={() => handleLinkClick(currentAnnouncement)}
                  className={classes.actionButton}
                >
                  {currentAnnouncement.linkText || "Saiba mais"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Box>
          {/* Timer de persistência */}
          {timeLeft > 0 && (
            <Typography className={classes.timer}>
              Aguarde {timeLeft}s para continuar
            </Typography>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end">
          {/* MODO CONFIRMAÇÃO: Dois botões específicos */}
          {currentAnnouncement.requiresAcknowledge ? (
            <>
              {/* Botão "Ver depois" - NÃO registra como lida */}
              <Button
                onClick={handleViewLater}
                variant="outlined"
                className={`${classes.actionButton} ${classes.viewLaterButton}`}
                disabled={timeLeft > 0}
                style={{ marginRight: 16 }}
              >
                Ver depois
              </Button>

              {/* Botão "Li e entendi" - Registra como lida */}
              <Button
                onClick={handleNext}
                variant="contained"
                className={`${classes.actionButton} ${classes.acknowledgeButton}`}
                disabled={timeLeft > 0}
              >
                {hasNext ? "Li e entendi - Próximo" : "Li e entendi"}
              </Button>
            </>
          ) : (
            /* MODO NORMAL: Botões padrão */
            <>
              {/* Botão de dispensar (apenas se permitido dispensar para sempre) */}
              {currentAnnouncement.allowDismissForever && (
                <Button
                  onClick={handleDismiss}
                  className={`${classes.actionButton} ${classes.dismissButton}`}
                  style={{ marginRight: 16 }}
                >
                  Não mostrar novamente
                </Button>
              )}

              {/* Botão de próximo/fechar */}
              <Button
                onClick={handleNext}
                variant="contained"
                className={`${classes.actionButton} ${classes.acknowledgeButton}`}
                disabled={timeLeft > 0}
              >
                {hasNext ? "Próximo" : "Fechar"}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>

    {/* Modal de imagem em tela cheia */}
    <Backdrop
      className={classes.imageBackdrop}
      open={Boolean(fullscreenImage)}
      onClick={handleCloseFullscreen}
    >
      <Box className={classes.fullscreenImageContainer}>
        <IconButton
          className={classes.closeFullscreenButton}
          onClick={handleCloseFullscreen}
          size="medium"
        >
          <CloseIcon />
        </IconButton>
        {fullscreenImage && (
          <img
            src={fullscreenImage}
            alt="Imagem em tela cheia"
            className={classes.fullscreenImage}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </Box>
    </Backdrop>
  </>
  );
};

export default AnnouncementDisplayModal;
